import { useEffect, useMemo, useState } from 'react'
import { useImmer } from 'use-immer'
import { UiBreadcrumb, ModalPasswordConfirmation, UiCard, UiCardBody, UiCardHeader, UiDataTable } from 'components/common'
import { Box, Button, Flex, FormControl, FormLabel, Heading, Input, Select, Text, useDisclosure } from '@chakra-ui/react'
import { getPayoutProjects, getPayoutRateForSalesUsers, runPayoutPayProjects } from 'api'
import { PAYOUT_FILTER_STATUSES, PROJECT_STAGES, ROLES, getAssetUrl, numberFormatter } from 'lib'
import { format as formatDate } from 'date-fns'
import { useAppContext } from 'hooks/useAppContext'

const BreadcrumbNav = [
  {
    label: 'Overage Portal',
    isCurrentPage: true
  }
]

const OVERAGE_COLUMNS = [
  {
    label: 'Sales User',
    orderBy: 'salesUserName',
    render: ({ salesUserName }) => salesUserName,
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  },
  {
    label: 'Order Date',
    orderBy: 'createdAt',
    render: ({ createdAt }) => formatDate(new Date(createdAt), 'MM/dd/yyyy'),
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  },
  {
    label: 'Project Name',
    orderBy: 'customerCompany',
    render: ({ customerCompany }) => customerCompany,
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  },
  {
    label: 'Deposit',
    orderBy: 'depositStatus',
    render: ({ depositStatus }) => depositStatus,
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  },
  {
    label: 'Permit',
    orderBy: 'permit',
    render: ({ permit }) => permit,
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  },
  {
    label: 'Overage',
    orderBy: 'overage',
    render: ({ overage }) => '$ ' + numberFormatter(overage),
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  },
  {
    label: 'Pay',
    orderBy: 'overage',
    render: ({ overage }) => '$ ' + numberFormatter(overage / 2),
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  },
  {
    label: 'Payment Status',
    orderBy: 'overagePaid',
    render: ({ overagePaid }) => overagePaid ? 'Paid' : 'Unpaid',
    style: {
      paddingLeft: '12px',
      paddingRight: '12px'
    }
  }
]

export const Overage = () => {
  const { createToast, currentUser } = useAppContext()
  const [tableConfig, setTableConfig] = useImmer({
    curPage: 1,
    totalPages: 1,
    totalRecords: 0,
    itemsPerPage: 10,
    search: '',
    orderBy: {
      key: 'customerCompany',
      direction: 'asc'
    }
  })
  const [isLoading, setIsLoading] = useState(false)
  const [projects, setProjects] = useState([])
  const [filterProjects, setFilterProjects] = useImmer({
    status: PAYOUT_FILTER_STATUSES.All,
    startDate: '2000-01-01',
    endDate: formatDate(new Date(), 'yyyy-MM-dd'),
    salespersonId: 'All'
  })
  const [salesUsers, setSalesUsers] = useState([])

  const disclosurePasswordConfirmation = useDisclosure()

  const [allSelected, setAllSelected] = useState(false)
  const [selectedProjects, setSelectedProjects] = useState([])

  useEffect(() => {
    const loadRateForSalesUsers = async () => {
      try {
        const response = await getPayoutRateForSalesUsers()
        setSalesUsers(response)
      } catch (err) {
        console.log(err)
      }
    }
    loadRateForSalesUsers()
  }, [])

  const load = async () => {
    setIsLoading(true)
    try {
      const response = await getPayoutProjects(
        true,
        filterProjects.status,
        filterProjects.startDate,
        filterProjects.endDate,
        filterProjects.salespersonId,
        tableConfig.curPage,
        tableConfig.itemsPerPage,
        tableConfig.search,
        tableConfig.orderBy.key,
        tableConfig.orderBy.direction
      )
      setProjects(response.rows)
      setTableConfig(draft => {
        draft.totalPages = response.totalPages
        draft.totalRecords = response.totalRecords
      })
      setIsLoading(false)
    } catch (err) {
      console.log(err)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [salesUsers, tableConfig.curPage, tableConfig.itemsPerPage, tableConfig.search, tableConfig.orderBy.key, tableConfig.orderBy.direction])

  const onSelect = (e, row) => {
    if (row) {
      setSelectedProjects([...selectedProjects, row])
    } else {
      if (allSelected) {
        setSelectedProjects([])
      }
      setAllSelected(!allSelected)
    }
  }

  const handleChange = (e) => {
    setFilterProjects(draft => {
      draft[e.target.name] = e.target.value
    })
  }

  const onClickRunReport = () => {
    load()
  }

  const onClickPayRun = () => {
    disclosurePasswordConfirmation.onOpen()
  }

  const handlePayRun = async () => {
    try {
      let selectedIds = []
      if (allSelected) {
        const response = await getPayoutProjects(true, filterProjects.status, filterProjects.startDate, filterProjects.endDate, filterProjects.salespersonId, 1, 99999999, '', 'customerCompany', 'asc')
        selectedIds = response.rows.filter(v => isQualifiedProject(v))
      } else {
        selectedIds = selectedProjects.map(v => v.id)
      }

      if (!selectedIds.length) {
        createToast('warning', 'Warning', 'No rows selected.')
        return
      }

      const response = await runPayoutPayProjects(true, selectedIds)
      window.open(getAssetUrl(response.pdf))
      createToast('success', 'Success', 'Overage has been paid for selected projects.')

      load()
      setAllSelected(false)
      setSelectedProjects([])
    } catch (err) {
      createToast('error', 'Error', err.response.data)
    }
    disclosurePasswordConfirmation.onClose()
  }

  const isQualifiedProject = (project) => {
    return !project.overagePaid && (project.stage === PROJECT_STAGES.MarginReview || project.stage === PROJECT_STAGES.Complete)
  }

  const customizedProjects = useMemo(() => {
    return projects.map(v => {
      return { ...v, canSelect: isQualifiedProject(v) }
    })
  }, [projects])

  return (
    <>
      <UiBreadcrumb items={BreadcrumbNav} />
      <Box>
        <Flex justifyContent="space-between" alignItems="center" my={8}>
          <Heading size="lg">Overage Portal</Heading>
        </Flex>
        <UiCard borderRadius={0}>
          <UiCardHeader bg="gray.500" p={4}>
            <Text fontWeight="bold" color="white">Projects</Text>
          </UiCardHeader>
          <UiCardBody>
            <Flex gap={4} alignItems="flex-end">
              <FormControl flex={1}>
                <FormLabel>Project Status</FormLabel>
                <Select name="status" value={filterProjects.status} onChange={handleChange}>
                  {Object.values(PAYOUT_FILTER_STATUSES).filter(status => status !== PAYOUT_FILTER_STATUSES.OnHold).map(status => <option key={status} value={status}>{status}</option>)}
                </Select>
              </FormControl>
              <FormControl flex={1}>
                <FormLabel>Sales User</FormLabel>
                <Select name="salespersonId" value={filterProjects.salespersonId} onChange={handleChange}>
                  <option value="All">All</option>
                  {salesUsers.map(user => <option key={user.id} value={user.id}>{user.firstname} {user.lastname}</option>)}
                </Select>
              </FormControl>
              <FormControl flex={1}>
                <FormLabel>Order Date</FormLabel>
                <Input type="date" name="startDate" value={filterProjects.startDate} onChange={handleChange} />
              </FormControl>
              <FormControl flex={1}>
                <FormLabel>Thru</FormLabel>
                <Input type="date" name="endDate" value={filterProjects.endDate} onChange={handleChange} />
              </FormControl>
              <Button size="sm" colorScheme="blue" flex={1} onClick={onClickRunReport}>Run Report</Button>
            </Flex>
            {currentUser?.role === ROLES.Admin && (
              <Flex justifyContent="center">
                <Button size="md" colorScheme="blue" onClick={onClickPayRun} mt={8} mb={4}>Add to Pay Run</Button>
              </Flex>
            )}
            <UiDataTable
              tableConfig={tableConfig}
              updateTableConfig={setTableConfig}
              rows={customizedProjects}
              rowKey="id"
              columns={OVERAGE_COLUMNS}
              isLoading={isLoading}
              selected={selectedProjects}
              onSelect={onSelect}
              allSelected={allSelected}
            />
          </UiCardBody>
        </UiCard>
        <ModalPasswordConfirmation
          {...disclosurePasswordConfirmation}
          heading={`Add to Pay Run - ${allSelected ? 'All qualified' : selectedProjects.length} rows selected`}
          btnConfirmLabel="Confirm"
          btnCancelLabel="Cancel"
          onConfirm={handlePayRun}
        />
      </Box>
    </>
  )
}
